/**
 * `visualization_schema.bindings`, built from what the author assigned
 * (CT-5641).
 *
 * The rev4 contract made this object self-describing. A binding used to be a
 * pointer — `{ field_key: 'dim_1' }` — into a query the backend had already
 * built from the entity, the groupers and the exposed columns; the names had
 * to be guessed right on both sides, and a slot bound to a name the query did
 * not have failed at publish with `UI_PRESENTATION_BINDING_FIELD_MISSING`.
 *
 * Now each item says which **source column** it reads (`field`), what is done
 * to it (`aggregate`, `time_grain`, `date_part`) and what the result is called
 * (`field_key`), and the sync *creates* the WIDGET field from that. There is
 * no name left to guess: this module and the generated query agree because
 * one of them is derived from the other.
 *
 * ## Two modes, one shape
 *
 * Who names the column depends on who built the dataset. With an
 * `EXISTING_QUERY` the binding has to carry `field` — the dataset's own
 * alias — beside `field_key`. In `ENTITY` mode the backend builds the
 * dataset from the same payload and fills `field` and `aggregate` behind the
 * binding; what it asks for instead is that **`field_key` be the alias of
 * the element the author chose**, because that is the name the generated
 * dataset will carry. `emitSource: false` is that mode: the operation still
 * decides the key, it just does not travel.
 *
 * ## What it refuses to do
 *
 * - **Invent a key the slot does not declare.** Every item is
 *   `additionalProperties: false`, so an assignment's extras are filtered
 *   through the slot's own property list. A `width_px` on a KPI is a rejected
 *   save, not a harmless hint.
 * - **Put an `aggregate` on a dimension or a column.** The role decides:
 *   `AGGREGATE` needs one, the other two must not carry one, and a slot that
 *   declares both (`CORRELATION.x`) is whichever the author chose.
 * - **Repeat a key across two slots.** It reads like the right thing — two
 *   slots on the same column are two references to one generated field — and
 *   the backend refuses it: a `BOARD` whose `lane` and a `card_fields` entry
 *   both resolved to `department` came back
 *   `UI_BINDING_FIELD_KEY_DUPLICATE (department)` (dev, 2026-10-20). Every
 *   item gets its own key, suffixed when the obvious one is taken.
 * - **Publish half a contract.** A required slot nobody filled returns `null`
 *   for the whole object, as the v1 builder did: a widget that saves as a
 *   draft and says it cannot be published is honest, one published against a
 *   partial contract is not.
 */

import {
  AGGREGATE_WITHOUT_FIELD,
  BINDING_ITEM_KEYS,
  WIDGET_FIELD_ROLES,
} from '../constants/widgetContract.js';
import {
  ROW_COUNT_FIELD_KEY,
  deriveFieldKey,
  isFieldKey,
  shapeFieldKey,
  uniqueFieldKey,
} from './widgetFieldKey.js';
import {
  readSlotModel,
  slotAcceptsRole,
  slotSupports,
  slotTakesAggregate,
} from './widgetSlotModel.js';

/** The item keys this module writes itself, whatever an assignment carries. */
const DERIVED_KEYS = new Set([
  ...BINDING_ITEM_KEYS.source,
  ...BINDING_ITEM_KEYS.operation,
  'label',
  'format',
  'is_visible',
]);

const isEmpty = (value) =>
  value === undefined || value === null || value === ''
  || (typeof value === 'object' && !Array.isArray(value) && Object.keys(value).length === 0);

/**
 * The operation an assignment asks for, given what the slot accepts.
 *
 * A dimension or a column takes none, whatever the form holds: a stale
 * aggregate left over from a slot change would turn a grouping into a measure
 * and change the grain of the whole query.
 */
const operationFor = (slot, assignment) => {
  if (!slotTakesAggregate(slot)) return {};
  const aggregate = assignment.aggregate ?? '';
  if (!aggregate) return {};
  if (!slot.aggregates.length || slot.aggregates.includes(aggregate)) return { aggregate };
  return {};
};

/** `time_grain` / `date_part`, each only where the slot declares it. */
const bucketFor = (slot, assignment) => ({
  ...(assignment.timeGrain && slotSupports(slot, 'time_grain')
    ? { time_grain: assignment.timeGrain } : {}),
  ...(assignment.datePart && slotSupports(slot, 'date_part')
    ? { date_part: assignment.datePart } : {}),
});

/**
 * One assignment → one binding item.
 *
 * @param {object} slot From `readSlotModel`.
 * @param {object} assignment `{ field, fieldKey?, aggregate?, timeGrain?,
 *   datePart?, label?, format?, isVisible?, extras? }`.
 * @param {{ taken: Set<string> }} keys The keys this widget has minted.
 * @returns {object|null} `null` when the assignment names no column and no
 *   row count — an item with neither reads as a binding the author made and
 *   the engine cannot resolve.
 */
const buildItem = (slot, assignment, keys, emitSource) => {
  const { aggregate } = operationFor(slot, assignment);
  const bucket = bucketFor(slot, assignment);
  const countsRows = aggregate === AGGREGATE_WITHOUT_FIELD;
  const field = countsRows ? '' : shapeFieldKey(assignment.field);

  if (!field && !countsRows) return null;
  /*
    A measure slot with no operation chosen is unfinished, not a raw column:
    the schema requires `aggregate` as soon as `field` is present. A slot that
    also accepts `COLUMN` is the exception — there, no operation is the
    author choosing the raw column, which is one of its two legal readings.
  */
  if (slotTakesAggregate(slot) && !aggregate
    && !slotAcceptsRole(slot, WIDGET_FIELD_ROLES.COLUMN)) return null;

  /*
    With the source left to the backend the key is not ours to derive: it has
    to be the alias of the element the author chose, which is what the
    generated dataset will call its column. `record_count` for a row count,
    which has no column to be named after.

    Unique either way, even for two slots on the same column: the backend
    refuses a repeated key (see the module note).
  */
  const base = emitSource
    ? (isFieldKey(assignment.fieldKey)
      ? assignment.fieldKey
      : deriveFieldKey({
        field, aggregate, timeGrain: bucket.time_grain, datePart: bucket.date_part,
      }))
    : field || (countsRows ? ROW_COUNT_FIELD_KEY : '');
  if (!base) return null;
  const fieldKey = uniqueFieldKey(base, keys.taken);
  keys.taken.add(fieldKey);

  /* Only the keys this slot declares; see the module note. */
  const extras = Object.fromEntries(
    Object.entries(assignment.extras ?? {})
      .filter(([key, value]) => slot.supports.includes(key)
        && !DERIVED_KEYS.has(key) && !isEmpty(value)),
  );

  return {
    ...(emitSource && field && slotSupports(slot, 'field') ? { field } : {}),
    field_key: fieldKey,
    ...(emitSource && aggregate ? { aggregate } : {}),
    ...bucket,
    ...(assignment.label && slotSupports(slot, 'label')
      ? { label: assignment.label } : {}),
    ...(!isEmpty(assignment.format) && slotSupports(slot, 'format')
      ? { format: assignment.format } : {}),
    ...(typeof assignment.isVisible === 'boolean' && slotSupports(slot, 'is_visible')
      ? { is_visible: assignment.isVisible } : {}),
    ...extras,
  };
};

const asList = (value) => {
  if (value === undefined || value === null) return [];
  return Array.isArray(value) ? value : [value];
};

/**
 * Build the whole `bindings` object.
 *
 * @param {object} params
 * @param {object|null} params.configSchema The type's `configuration_schema`.
 * @param {object} params.assignments `{ [slotKey]: assignment | assignment[] }`,
 *   as `assignSlotsFromDataStep` produces them. A slot with no assignment is
 *   simply absent, which is what an optional slot means.
 * @param {object} [params.nonField] Non-field bindings already built — today
 *   only `{ time }`, from `widgetTimeBinding`. Merged as given.
 * @param {boolean} [params.emitSource] Whether the items carry `field` and
 *   `aggregate`. `false` in `ENTITY` mode, where the backend fills both and
 *   `field_key` must be the chosen element's alias.
 * @returns {object|null} The bindings, or `null` when the type publishes no
 *   readable contract or a required slot is unfilled.
 */
export function buildWidgetBindings({
  configSchema,
  assignments = {},
  nonField = {},
  emitSource = true,
}) {
  const model = readSlotModel(configSchema);
  if (!model.slots.length) return null;

  const keys = { taken: new Set() };
  const bindings = {};

  model.slots.forEach((slot) => {
    const items = asList(assignments[slot.key])
      .map((assignment) => buildItem(slot, assignment ?? {}, keys, emitSource))
      .filter(Boolean);

    if (!items.length) return;
    // `maxItems` is the contract's, not the form's: a list longer than the
    // slot accepts is rejected whole, so it is cut here with the author's own
    // order preserved.
    bindings[slot.key] = slot.isArray ? items.slice(0, slot.max ?? items.length) : items[0];
  });

  const missing = model.slots.filter((slot) => {
    if (!slot.required) return false;
    const value = bindings[slot.key];
    if (!value) return true;
    return slot.isArray && value.length < (slot.min ?? 1);
  });
  if (missing.length) return null;

  const resolved = { ...bindings, ...Object.fromEntries(
    Object.entries(nonField).filter(([, value]) => !isEmpty(value)),
  ) };

  return Object.keys(resolved).length ? resolved : null;
}

export default buildWidgetBindings;
